import { uiConfigurationsSlice } from '@lmt/unicorn-shared/app/features/ui-configurations/reducer';
import { variablesSlice } from '@lmt/unicorn-shared/app/features/variables/reducer';
import { logout } from '@lmt/unicorn-shared/app/store/actions';
import {
  combineReducers,
  configureStore,
  EnhancedStore,
} from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { CombinedState, Reducer } from 'redux';
import {
  createTransform,
  PersistConfig,
  persistReducer,
  persistStore,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import { getType } from 'typesafe-actions';
import { globalFormsSlice } from '../features/global-forms/reducer';
import {
  measurementListenerMiddleware,
  measurementSlice,
} from '../features/measurements/reducer';
import { navigationSlice } from '../features/navigation/reducer';
import { settingsSlice } from '../features/settings/reducer';
import { patientSearchSlice } from '../features/patientSearchSlice';
import { NavigationState } from '../features/navigation/types';
import { logForDebug } from '../helpers/logging';
import { isLastLoggedInTimeMoreThanNSecondsAgo } from '../components/screen-plugs/LastLoginTimeProvider';
import { pages } from '../constants/sharedRoutes';
import { updateTodayInCalendarUrl } from '../components/navigation/types';

const combinedReducer = combineReducers({
  measurement: measurementSlice.reducer,
  globalForms: globalFormsSlice.reducer,
  navigation: navigationSlice.reducer,
  settings: settingsSlice.reducer,
  variables: variablesSlice.reducer,
  patientSearch: patientSearchSlice.reducer,
  uiConfigurations: uiConfigurationsSlice.reducer,
});

export type GlobalState = ReturnType<typeof combinedReducer>;

const rootReducer: Reducer<CombinedState<GlobalState>> = (state, action) => {
  if (action.type === getType(logout)) {
    if (state) {
      state = {
        ...state,
        navigation: {
          // we should preserve opened tabs on logout as per `#46420: Store previous opened tabs on logout and on autologout`
          ...state.navigation,
          tabStateChangeSubscribers: [],
        },
      };
    }
  }

  return combinedReducer(state, action);
};

/*
 * this transform removes empty elements from `navigation.tabs` and `navigation.defaultTabs`
 * this fixes the Bug 62795: undefined element inside navigations.tabs
 */
const removeEmptyTabsTransform = createTransform(
  undefined,
  (outboundState: NavigationState, key) => {
    outboundState.defaultTabs = outboundState.defaultTabs.filter((x) => x);
    outboundState.tabs = outboundState.tabs.filter((x) => x);
    return outboundState;
  },
  { whitelist: ['navigation'] },
);

const resetCalendarDateIntervalInSeconds = 2 * 60 * 60; // 2 hours
/*
 * this transform resets the `currentDate` in all calendar tabs, if user was logged out for 2 hours
 */
const resetDateInCalendarTabsTransform = createTransform(
  undefined,
  (outboundState: NavigationState, key) => {
    if (
      !isLastLoggedInTimeMoreThanNSecondsAgo(resetCalendarDateIntervalInSeconds)
    )
      return outboundState;

    outboundState.defaultTabs = outboundState.defaultTabs.filter((x) => x);

    outboundState.tabs.forEach((tab) => {
      if (pages.calendarTab.matchPath(tab.path)) {
        const newLink = updateTodayInCalendarUrl(tab.search ?? '', true);
        tab.search = newLink;
      }
    });
    outboundState.defaultTabs.forEach((tab) => {
      if (pages.calendar.matchPath(tab.path)) {
        const newLink = updateTodayInCalendarUrl(tab.search ?? '', true);
        tab.search = newLink;
      }
    });

    return outboundState;
  },
  { whitelist: ['navigation'] },
);

const persistConfig: PersistConfig<GlobalState> = {
  key: 'root',
  storage,
  whitelist: [
    'auth',
    'uiConfigurations',
    'variables',
    'settings',
    'navigation',
    'dictionaries',
    'roles',
    'measurementTypes',
  ] as (keyof GlobalState)[] as string[],
  transforms: [removeEmptyTabsTransform, resetDateInCalendarTabsTransform],
};

// TODO: Remove after Release 4
// Needed to support changing persist-key from username (email) to userId.
// Without the migration user loses their local settings when updating from Release 2 to Release 3
function migratePersistedData(oldKey: string, newKey: string) {
  if (!oldKey || !newKey || oldKey === newKey) {
    return; // No migration needed
  }

  try {
    const oldStorageKey = `persist:${oldKey}`;
    const newStorageKey = `persist:${newKey}`;

    const oldData = localStorage.getItem(oldStorageKey);
    const newData = localStorage.getItem(newStorageKey);

    if (oldData && !newData) {
      console.log(`Migrating persisted data from ${oldKey} to ${newKey}`);

      localStorage.setItem(newStorageKey, oldData);

      localStorage.removeItem(oldStorageKey);

      console.log('Data migration completed successfully');
    }
  } catch (error) {
    console.warn('Failed to migrate persisted data:', error);
    // Don't throw - let the app continue with fresh state if migration fails
  }
}

export function configureUniStore(userId: string, username: string) {
  migratePersistedData(username, userId);

  if (userId !== 'root' || username !== 'root')
    // to make debugging easier
    logForDebug(
      'localstorage',
      localStorage.getItem(`persist:${userId}`),
      userId,
      username,
    );

  const store = configureStore({
    reducer: persistReducer({ ...persistConfig, key: userId }, rootReducer),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }).concat(measurementListenerMiddleware.middleware),
    enhancers: [],
  });

  const persistor = persistStore(store);
  _getState = store.getState;
  _dispatch = store.dispatch;
  return { store, persistor };
}

let _dispatch: UniDispatch;
export function getDispatch() {
  return _dispatch;
}

let _getState: () => GlobalState;
export function getState(): GlobalState {
  return _getState();
}

export function replacePersistedReducer(store: EnhancedStore, user: string) {
  store.replaceReducer(
    persistReducer(
      { ...persistConfig, key: user ? 'root:' + user : 'root' },
      rootReducer,
    ),
  );
}

export type UniDispatch = ReturnType<
  typeof configureUniStore
>['store']['dispatch'];
export const useUniDispatch = () => useDispatch<UniDispatch>();
export const useUniSelector: TypedUseSelectorHook<GlobalState> = useSelector;
